/**
 * Back-to-top floating button (Task 5). Watches a scroll container and, once the
 * user has scrolled past a threshold, shows an unobtrusive round button that
 * smooth-scrolls back to the top. Used on the full-page-scrolled lists (the app
 * shell's <main> is the scroll container). Respects prefers-reduced-motion and
 * sits below popovers/drawers so it never obstructs a modal surface.
 */
import { useEffect, useState, type RefObject } from "react";
import { Icon } from "./Icon.js";

const THRESHOLD_PX = 400;

export function BackToTop({ scrollRef }: { scrollRef: RefObject<HTMLElement | null> }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => setShow(el.scrollTop > THRESHOLD_PX);
    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, [scrollRef]);

  if (!show) return null;

  const toTop = () => {
    const el = scrollRef.current;
    if (!el) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    el.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
  };

  return (
    <button type="button" className="back-to-top" onClick={toTop} aria-label="Back to top" title="Back to top">
      <Icon name="chevronUp" />
    </button>
  );
}
